import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useLanguageStore } from '$/store/languageStore';
// eslint-disable-next-line import/no-cycle
import { replaceTemplateParams } from '$/utils';
import useUploadThreadStore from '$/store/useUploadThreadStore';
import { createSmartcatTranslation } from '$/store/languageStore/smartcatTranslation';
import Styles from './index.module.scss';
import IconFont from '../IconFont';

// 类型定义
export type MessageType = 'info' | 'success' | 'error' | 'warning' | 'loading';

export type MessageContent =
  | string
  | React.ReactNode
  | { key: string; params?: Record<string, any>; };

interface MessageItem {
  id: number;
  type: MessageType;
  content: MessageContent;
  duration: number;
  closable?: boolean;
  onClose?: () => void;
  action?: MessageAction;
}

export interface MessageAction {
  label: string;
  onClick: () => void;
}

export interface MessageConfig {
  content: MessageContent;
  duration?: number;
  closable?: boolean;
  onClose?: () => void;
  action?: MessageAction;
}

// 工具类型
type MessageArgs =
  | [MessageContent]
  | [MessageContent, number]
  | [MessageContent, number, () => void]
  | [MessageConfig];

// 消息类型图标映射
const MESSAGE_TYPE = {
  info: { icon: 'infoCircleFill' },
  success: { icon: 'checkCircleFill' },
  error: { icon: 'exclamationMarkTriangleFill' },
  warning: { icon: 'exclamationMarkTriangleFill' },
  loading: { icon: 'loadingCircleBold' },
};

let addMessage: (msg: Omit<MessageItem, 'id'>) => number;
let removeMessage: (id: number) => void;

function isConfigObject(arg: any): arg is MessageConfig {
  return arg && typeof arg === 'object' && !React.isValidElement(arg) && 'content' in arg;
}

function normalizeArgs(args: MessageArgs): MessageConfig {
  if (isConfigObject(args[0])) {
    return args[0];
  }

  const content = args[0] as MessageContent;
  const duration = (args.length > 1 ? args[1] : 2) as number;
  const onClose = (args.length > 2 ? args[2] : undefined) as (() => void) | undefined;

  return {
    content,
    duration,
    onClose,
  };
}

/** 创建指定类型消息，并透传可关闭等展示配置。 */
function createMessage(type: MessageType) {
  return (...args: MessageArgs) => {
    const {
      content, duration = 2, closable, onClose, action,
    } = normalizeArgs(args);
    return addMessage?.({
      type, content, duration, closable, onClose, action,
    });
  };
}

function isMessageWithKey(msg: any): msg is { key: string; params?: Record<string, any>; } {
  return msg && typeof msg === 'object' && 'key' in msg;
}

/** 渲染全局消息队列，并支持按消息配置手动关闭。 */
export function MessageProvider() {
  const [messages, setMessages] = useState<MessageItem[]>([]);
  const languageStore = useLanguageStore((state) => state.languageStore);
  const uploadThread = useUploadThreadStore((state) => state.uploadThread);
  const closeLabel = languageStore.smartcatTranslationText((v) => v.close) || 'Close';
  useEffect(() => {
    let key = 0;

    /** 将新消息追加到队列，并按 duration 自动移除。 */
    addMessage = function addMessageHandler(msg: Omit<MessageItem, 'id'>): number {
      const {
        type, content, duration = 2, closable, onClose, action,
      } = msg;
      key += 1;
      const id = key;

      setMessages((prev) => [...prev, {
        id, type, content, duration, closable, onClose, action,
      }]);

      if (duration !== 0) {
        setTimeout(() => removeMessage(id), duration * 1000);
      }
      return id;
    };

    removeMessage = function removeMessageHandler(id: number) {
      setMessages((prev) => {
        const target = prev.find((msg) => msg.id === id);
        if (target?.onClose) target.onClose();
        return prev.filter((msg) => msg.id !== id);
      });
    };
  }, []);

  const translateMessage = (message: MessageContent): React.ReactNode => {
    // 1: 对象形式 { key, params }
    if (isMessageWithKey(message)) {
      const { key, params } = message;
      const template = languageStore.smartcatTranslationText(
        (v) => v[key as keyof ReturnType<typeof createSmartcatTranslation>],
      );
      return replaceTemplateParams(template || key, params);
    }

    // 2: 字符串形式，尝试作为 key 翻译
    if (typeof message === 'string') {
      const template = languageStore.smartcatTranslationText(
        (v) => v[message as keyof ReturnType<typeof createSmartcatTranslation>],
      );

      return template || message;
    }

    // 3: ReactNode 直接返回
    return message;
  };
  return (
    <div
      className={Styles.messageWrapper}
      style={{
        top: `${32 + Object.keys(uploadThread).length * (52 + 5)}px`,
      }}
    >
      {messages?.map((msg) => (
        <div key={msg.id} className={`${Styles.message} ${Styles[msg.type]}`}>
          <IconFont
            name={MESSAGE_TYPE[msg.type].icon}
            fontSize={16}
            className={classNames(Styles.icon, {
              [Styles.loading]: msg.type === 'loading',
            })}
          />
          <span className={Styles.content}>
            {(typeof msg.content === 'string' || isMessageWithKey(msg.content)) && !React.isValidElement(msg.content)
              ? translateMessage(msg.content)
              : msg.content}
          </span>
          {msg.action && (
            <button
              type="button"
              className={Styles.actionButton}
              onClick={(e) => {
                e.stopPropagation();
                msg.action?.onClick();
              }}
            >
              {msg.action.label}
            </button>
          )}
          {msg.closable && (
            <button
              type="button"
              className={Styles.closeButton}
              aria-label={closeLabel}
              onClick={(event) => {
                event.stopPropagation();
                removeMessage(msg.id);
              }}
            >
              <IconFont name="crossFill" fontSize={16} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export const message = {
  info: createMessage('info'),
  success: createMessage('success'),
  error: createMessage('error'),
  warning: createMessage('warning'),
  /** 创建默认常驻的加载消息，并返回主动关闭函数。 */
  loading(...args: MessageArgs) {
    const {
      content, duration = 0, closable, onClose,
    } = normalizeArgs(args);
    const id = addMessage?.({
      type: 'loading', content, duration, closable, onClose,
    });
    return () => removeMessage?.(id!);
  },
  destroy(id: number) {
    removeMessage?.(id);
  },
};
